import * as React from 'react'
import {
  ChevronRightIcon,
  ComponentInstanceIcon,
  PlusIcon,
} from '@radix-ui/react-icons'
import {
  blockTypesRecord,
  prettifyBlockType,
} from '@basehub/data/replicache/index'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/sotano-kit/primitives/dropdown-menu'
import { getId } from '@basehub/utils/id/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSlashCommandStore } from '~/lib/tiptap/extensions/comment/extension-slash-command'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { BlockIcon } from '~/components/block-icon'
import { Button } from '~/sotano-kit/primitives/button'
import { useSelectedView } from '../views/use-selected-view'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { Input } from '~/sotano-kit/primitives/input'
import { HEADER_HEIGHT, ROW_HEIGHT } from '../views/table/theme'
import { useEditorStore } from '../../../editor-store'
import { clsx } from 'clsx'

import type { AtLeast } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import type {
  Block,
  ListBlock,
  MediaBlock,
} from '@basehub/data/replicache/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

const mediaAliases = ['image', 'video', 'audio', 'file'] as const

export const AddColumnMenu: React.FC<{
  block: ListBlock
  path: string
  className?: string
}> = ({ path, block, className }) => {
  const { data } = useRepoReplicache()
  const commands = useSlashCommandStore((s) => s.commands)
  const replicache = data?.replicache
  const components = commands?.find((command) => command.id === 'components')
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const [selectedView] = useSelectedView(block, { request: 'table' })
  const [query, setQuery] = React.useState('')
  const itemsRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const newColumnButtonRef = React.useRef<HTMLButtonElement>(null)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)

  const onSelectNewField = React.useCallback(
    async (newBlock: AtLeast<Block, 'type' | 'id'>) => {
      if (!replicache || !blockTreeManager) return

      const newBlockData =
        await replicache.mutate.createColumnAndAddToColumnMetadata({
          data: {
            ...(newBlock as any),
            id: newBlock.id,
            title: newBlock.title ?? '',
            type: newBlock.type,
          },
          listBlockId: block.id,
          viewId: selectedView?.id,
          targetBlockId_withinTree: blockTreeManager.getNode(
            block.typeOptions.targetBlockId,
            { required: true }
          ).value.id,
          isoNow: new Date().toISOString(),
          path,
          idSeed: getId(),
        })

      return newBlockData
    },
    [
      block.id,
      block.typeOptions.targetBlockId,
      blockTreeManager,
      path,
      replicache,
      selectedView,
    ]
  )

  const possibleInstances = React.useMemo(() => {
    return components?.commands
      .slice(1, components?.commands.length)
      .reduce<React.ReactNode[]>((acum, component) => {
        if (component.id === block.typeOptions.targetBlockId) {
          return acum
        }

        acum.push(
          <DropdownMenuItem
            key={component.id}
            className="space-x-2 text-dark-dim"
            onSelect={async () => {
              if (!components?.commands) {
                throw new Error('Component not found')
              }

              const id = getId()
              await onSelectNewField({
                id,
                type: blockTypesRecord.instance,
                title: `${component.title}'s instance`,
                typeOptions: {
                  targetBlockId: idToSourceBlockId(component),
                },
              })
              setIsRenamingBlockId(id)
            }}
          >
            <ComponentInstanceIcon className="text-dark-muted" />
            <span>{component.title}</span>
          </DropdownMenuItem>
        )

        return acum
      }, [])
  }, [
    block.typeOptions.targetBlockId,
    components?.commands,
    setIsRenamingBlockId,
    onSelectNewField,
  ])

  const queryMatches = (title: string) => {
    return title.toLowerCase().includes(query.toLowerCase())
  }

  const blockTypesRecordAsArray = [
    ...Object.values(blockTypesRecord),
    ...mediaAliases,
  ]

  const NOT_ALLOWED_LIST_BLOCK_TYPES = [
    blockTypesRecord.document,
    blockTypesRecord.comment,
    blockTypesRecord.workflow,
    blockTypesRecord.root,
    blockTypesRecord.agent,
  ]
  const filteredTypes = blockTypesRecordAsArray.filter((type) => {
    if (NOT_ALLOWED_LIST_BLOCK_TYPES.includes(type)) return false
    if (type === blockTypesRecord.list) return queryMatches('Collection')

    return queryMatches(type)
  })

  React.useEffect(() => {
    //  add event listener for pointer down
    const handlePointerUp = () => {
      if (!newColumnButtonRef.current) return
      newColumnButtonRef.current.style.removeProperty('pointer-events')
      newColumnButtonRef.current.style.removeProperty('cursor')
    }

    const handlePointerDown = (e: MouseEvent) => {
      if (!newColumnButtonRef.current) return
      if (e.target === newColumnButtonRef.current) return
      if (newColumnButtonRef.current?.contains(e.target as Node)) return

      newColumnButtonRef.current.style.pointerEvents = 'none'
      newColumnButtonRef.current.style.cursor = 'col-resize'
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('pointerup', handlePointerUp)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('pointerup', handlePointerUp)
    }
  }, [])

  return (
    <DropdownMenu
      onOpenChange={(o) => {
        if (!o) setQuery('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          ref={newColumnButtonRef}
          unstyled
          focusStyled
          style={{ height: HEADER_HEIGHT }}
          className={clsx(
            'mr-auto px-2 flex h-9 relative z-50 cursor-default items-center justify-start bg-dark-shade hover:bg-dark-shade-hover rounded-none !outline-dark-control-bg-dark-shade focus-visible:outline-offset-0 radix-state-open:opacity-100',
            className
          )}
        >
          <PlusIcon width={16} height={16} className="text-dark-dim" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        sticky="always"
        collisionPadding={20}
        className="!p-0 w-56 !bg-dark-base rounded-lg"
        side="bottom"
        onKeyDown={(e) => {
          if (
            ![
              'ArrowRight',
              'ArrowLeft',
              'ArrowDown',
              'ArrowUp',
              'Enter',
              'Escape',
            ].includes(e.key)
          ) {
            if (document.activeElement !== inputRef.current) {
              e.preventDefault()
              if (!inputRef.current) return
              const inputIsALetter = e.key.length === 1
              if (!inputIsALetter && e.key !== 'Backspace') return
              if (e.key === 'Backspace') {
                const popedInputValue = inputRef.current.value.slice(0, -1)
                inputRef.current.value = popedInputValue
              } else {
                inputRef.current.value = (inputRef.current.value ?? '') + e.key
              }
              inputRef.current.focus()
            }
          }

          if (
            e.key === 'ArrowUp' &&
            e.target === itemsRef.current?.firstChild
          ) {
            e.preventDefault()
            e.stopPropagation()
            inputRef.current?.focus()
          }
        }}
      >
        <div className="pt-1.5 px-1.5">
          <Input
            size="sm"
            autoFocus
            placeholder="Filter"
            ref={inputRef}
            onChange={(e) => {
              e.stopPropagation()
              setQuery(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation()
              }

              if (e.key === 'ArrowUp') {
                e.preventDefault()
              } else if (e.key === 'ArrowDown') {
                e.preventDefault()
                const firstItem = itemsRef.current?.firstChild
                if (!firstItem || !(firstItem instanceof HTMLElement)) {
                  return
                }
                firstItem.focus()
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const firstItem = itemsRef.current?.firstChild
                if (!firstItem || !(firstItem instanceof HTMLElement)) {
                  return
                }
                firstItem.click()
              }
            }}
          />
        </div>

        <hr className="border-dark-border-solid my-1.5 -mx-2" />

        <ScrollArea
          orientation="vertical"
          className="px-1.5 pb-1.5"
          variant="subtle"
        >
          <div className="max-h-72 xl:max-h-80 2xl:max-h-96" ref={itemsRef}>
            {!filteredTypes.length ? (
              <span className="text-xs text-dark-muted px-1 truncate flex">
                No results for&nbsp;
                <span className="font-medium text-dark-dim truncate">
                  "{query}"
                </span>
              </span>
            ) : (
              filteredTypes.map((type) => {
                if (type === blockTypesRecord.instance) {
                  return (
                    <DropdownMenuSub key={type}>
                      <DropdownMenuSubTrigger className="flex items-center justify-between text-xs leading-4 text-dark-dim hover:bg-dark-shade-hover focus:bg-dark-shade-hover">
                        <div className="flex items-center space-x-2">
                          <BlockIcon
                            block={{ type: blockTypesRecord.instance }}
                            iconProps={{
                              className: 'flex-shrink-0 text-dark-faint',
                              width: 16,
                              height: 16,
                            }}
                          />
                          <span>Instance</span>
                        </div>

                        <ChevronRightIcon
                          width={14}
                          height={14}
                          className="inline-block text-dark-muted"
                        />
                      </DropdownMenuSubTrigger>

                      <DropdownMenuPortal>
                        <DropdownMenuSubContent sideOffset={2} alignOffset={-5}>
                          {possibleInstances?.length ? (
                            possibleInstances
                          ) : (
                            <p className="text-dark-muted text-xs px-2 py-1">
                              Nothing yet
                            </p>
                          )}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )
                }

                const title = prettifyBlockType(type)

                return (
                  <DropdownMenuItem
                    key={type}
                    className="flex items-center space-x-2 text-xs leading-4 text-dark-dim hover:bg-dark-shade-hover focus:bg-dark-shade-hover"
                    onSelect={async () => {
                      const id = getId()
                      await onSelectNewField({
                        id,
                        type: mediaAliases.includes(type) ? 'media' : type,
                        title,
                        ...(mediaAliases.includes(type)
                          ? ({
                              constraints: {
                                allowedMimeTypes: { [type]: true },
                                isRequired: false,
                                maxSize: null,
                              } satisfies MediaBlock['constraints'],
                            } as any)
                          : {}),
                      })
                      setIsRenamingBlockId(id)
                    }}
                  >
                    <BlockIcon
                      block={{
                        type: mediaAliases.includes(type) ? 'media' : type,
                        ...(mediaAliases.includes(type)
                          ? ({
                              constraints: {
                                allowedMimeTypes: { [type]: true },
                                isRequired: false,
                                maxSize: null,
                              } satisfies MediaBlock['constraints'],
                            } as any)
                          : {}),
                      }}
                      iconProps={{
                        className: 'flex-shrink-0 text-dark-faint',
                        width: 16,
                        height: 16,
                      }}
                    />
                    <span className="capitalize-first">{title}</span>
                  </DropdownMenuItem>
                )
              })
            )}
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const RightColumn = React.forwardRef<
  HTMLDivElement,
  { listLength: number; children?: React.ReactNode; className?: string }
>(({ listLength, children, className }, ref) => {
  return (
    <div ref={ref} className={clsx('flex-1 bg-dark-base', className)}>
      <div
        style={{ height: HEADER_HEIGHT }}
        className="bg-dark-shade border-y border-dark-border-solid box-content"
      >
        {children}
      </div>

      {Array.from({ length: listLength }).map((_, index) => {
        return (
          <hr
            key={index}
            className={clsx(
              'border-dark-border-solid last:!mb-0',
              index === 0 && '-mt-px'
            )}
            style={{ marginBottom: ROW_HEIGHT - 1 }}
          />
        )
      })}
    </div>
  )
})
